import type { EditorView } from '@codemirror/view';
import {
  bold,
  bulletList,
  codeBlock,
  heading,
  imageRef,
  inlineCode,
  italic,
  link,
  orderedList,
  quote,
  strikethrough,
  taskList,
} from '../lib/markdownCommands';
import './EditorToolbar.css';

interface Props {
  /** Null until CodeMirror has mounted; the buttons stay inert until then. */
  view: EditorView | null;
  saveLabel: string;
  onBack?: () => void;
  title: string;
  /** Panel toggles live at the far right, beside the save indicator. */
  outlineOpen: boolean;
  propertiesOpen: boolean;
  onToggleOutline: () => void;
  onToggleProperties: () => void;
}

interface Action {
  id: string;
  label: string;
  hint: string;
  run: (view: EditorView) => void;
}

const GROUPS: Action[][] = [
  [
    { id: 'h1', label: 'H₁', hint: 'Heading 1', run: (v) => heading(v, 1) },
    { id: 'h2', label: 'H₂', hint: 'Heading 2', run: (v) => heading(v, 2) },
    { id: 'h3', label: 'H₃', hint: 'Heading 3', run: (v) => heading(v, 3) },
  ],
  [
    { id: 'bold', label: 'B', hint: 'Bold', run: bold },
    { id: 'italic', label: 'I', hint: 'Italic', run: italic },
    { id: 'strike', label: 'S', hint: 'Strikethrough', run: strikethrough },
  ],
  [
    { id: 'bullet', label: '•—', hint: 'Bulleted list', run: bulletList },
    { id: 'ordered', label: '1.', hint: 'Numbered list', run: orderedList },
    { id: 'task', label: '☑', hint: 'Task list', run: taskList },
  ],
  [
    { id: 'code', label: '‹›', hint: 'Code', run: inlineCode },
    { id: 'block', label: '{ }', hint: 'Code block', run: codeBlock },
    { id: 'link', label: '⛓', hint: 'Link', run: link },
    { id: 'image', label: '▣', hint: 'Inline image', run: (v) => imageRef(v) },
    { id: 'quote', label: '❝', hint: 'Quote', run: quote },
  ],
];

/**
 * Minimal top toolbar — formatting and insert actions only.
 *
 * Every button writes markdown into the document, so the same result is
 * reachable by typing. The toolbar is a convenience, never the only route.
 */
export function EditorToolbar({
  view,
  saveLabel,
  onBack,
  title,
  outlineOpen,
  propertiesOpen,
  onToggleOutline,
  onToggleProperties,
}: Props) {
  return (
    <div className="etb">
      {onBack && (
        <button type="button" className="etb__back" onClick={onBack} title="Back">
          ←
        </button>
      )}
      <span className="etb__title truncate">{title || 'Untitled'}</span>

      <div className="etb__actions">
        {GROUPS.map((group, i) => (
          <div className="etb__group" key={i}>
            {group.map((action) => (
              <button
                key={action.id}
                type="button"
                className="etb__button"
                title={action.hint}
                aria-label={action.hint}
                disabled={!view}
                // Keep focus in the editor: losing the selection would make
                // every button apply to nothing.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => view && action.run(view)}
              >
                {action.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      <span className="etb__save">{saveLabel}</span>

      <div className="etb__group etb__group--panels">
        <button
          type="button"
          className={`etb__button${outlineOpen ? ' is-on' : ''}`}
          title="Outline"
          aria-pressed={outlineOpen}
          onClick={onToggleOutline}
        >
          ☰
        </button>
        <button
          type="button"
          className={`etb__button${propertiesOpen ? ' is-on' : ''}`}
          title="Properties"
          aria-pressed={propertiesOpen}
          onClick={onToggleProperties}
        >
          ⋮⋮
        </button>
      </div>
    </div>
  );
}
